import { configureStore, createSlice } from '@reduxjs/toolkit';
import { lookupApi } from './services/lookupApi';
import { translateApi } from './services/translateApi';

const storeSlice = createSlice({
  name: 'store',
  initialState: {
    alerts: {
      expired_m2m_subscriptions: {},
      expired_plan_subscriptions: {},
      pending_info: {},
      alarms_m2m: {}
    }
  },
  reducers: {
    saveExpiredM2MSubscriptions: (state, action) => {
      state.alerts.expired_m2m_subscriptions = action.payload;
    },
    saveExpiredPlanSubscriptions: (state, action) => {
      state.alerts.expired_plan_subscriptions = action.payload;
    },
    savePendingInfo: (state, action) => {
      state.alerts.pending_info = action.payload.pending;
    },
    saveM2MInfo: (state, action) => {
      state.alerts.alarms_m2m = action.payload.alarmsActived;
    },
  },
});

/**
 * Escribe una traducción en el borrador y marca la celda como tocada.
 */
function writeTranslation(state, { id, code, value }) {
  const param = state.draft.byId[id];
  if (!param) return;

  // Si no existía el objeto de traducción, lo creamos
  if (!param.l10n) param.l10n = { translations: {} };
  if (!param.l10n.translations) param.l10n.translations = {};
  if (!param.l10n.translations[code]) param.l10n.translations[code] = { description: '' };

  param.l10n.translations[code].description = value;

  const key = `${id}|${code}`;
  if (value === (state.original[key] ?? '')) {
    delete state.dirtyKeys[key];
  } else {
    state.dirtyKeys[key] = true;
  }
}

/** Estado inicial del proceso de traducción masiva. */
const emptyBulk = { state: 'idle', total: 0, done: 0, errors: 0 };

// Estado propio del traductor de librerías: lo que NO viene del servidor.
// Los datos de servidor los gestiona translateApi (RTK Query).
const translateSlice = createSlice({
  name: 'translate',
  initialState: {
    contextId: null,
    // Borrador de edición: lo que el usuario lleva escrito y aún no ha guardado.
    draft: {
      byId: {},   // { [paramId]: parámetro completo, tal como lo devuelve la API }
      ids: [],    // orden original
    },
    original: {},    // { 'id|idioma': valor tal como vino del servidor }
    dirtyKeys: {},   // celdas que difieren del original;
    bulk: { ...emptyBulk },   // estado y progreso del proceso de traducción masiva
  },
  reducers: {
    setContextId: (state, action) => { state.contextId = action.payload; },

    /**
     * Vuelca los parámetros de la API al borrador, tal como vienen.
     * Se clonan sólo los tres niveles que se editan
     */
    loadDraft: (state, action) => {
      const byId = {};
      const ids = [];
      const original = {};

      for (const param of action.payload ?? []) {
        ids.push(param.id);

        const translations = {};
        for (const [code, tr] of Object.entries(param.l10n?.translations ?? {})) {
          translations[code] = { ...tr };
          original[`${param.id}|${code}`] = String(tr.description ?? '');
        }

        byId[param.id] = {
          ...param,
          l10n: { ...param.l10n, translations },
        };
      }

      state.draft = { byId, ids };
      state.original = original;
      state.dirtyKeys = {};
    },

    /** Resetea el borrador a su estado inicial, descartando cambios. */
    resetDraft: (state) => {
      state.draft = { byId: {}, ids: [] };
      state.original = {};
      state.dirtyKeys = {};
      state.bulk = { ...emptyBulk };
    },

    /** Edición manual de una celda. */
    setTranslation: (state, action) => writeTranslation(state, action.payload),

    /** Tras un guardado correcto: el borrador ya coincide con el servidor. */
    markDraftSaved: (state) => {
      state.dirtyKeys = {};
    },

    /** Arranca el proceso: `payload` es el total de traducciones a hacer. */
    bulkStart: (state, action) => {
      state.bulk = { state: 'running', total: action.payload, done: 0, errors: 0 };
    },

    /**
     * Aplica un lote de resultados de golpe. Es el volcado del buffer
     */
    applyTranslations: (state, action) => {
      for (const item of action.payload) {
        writeTranslation(state, item);
      }
      state.bulk.done += action.payload.length;
    },

    /** Si falla una traducción, cuenta como procesada, y como error. */
    bulkError: (state) => {
      state.bulk.errors += 1;
      state.bulk.done += 1;
    },

    bulkStopping: (state) => { state.bulk.state = 'stopping'; },
    bulkFinish: (state) => { state.bulk.state = 'idle'; },
    bulkPausing: (state) => { state.bulk.state = 'pausing'; },
    bulkPaused: (state) => { state.bulk.state = 'paused'; },

    /**
     * Continuar tras una pausa. El total se recalcula porque lo que queda por
     * traducir puede haber cambiado: celdas rellenadas a mano durante la pausa.
     */
    bulkResume: (state, action) => {
      state.bulk.state = 'running';
      if (action.payload?.total != null) state.bulk.total = action.payload.total;
    },
  },
});

const store = configureStore({
  reducer: {
    store: storeSlice.reducer,
    translate: translateSlice.reducer,
    [lookupApi.reducerPath]: lookupApi.reducer,
    [translateApi.reducerPath]: translateApi.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(lookupApi.middleware, translateApi.middleware),
});

export const { saveExpiredM2MSubscriptions, saveExpiredPlanSubscriptions, savePendingInfo, saveM2MInfo } = storeSlice.actions;

export const {
  setContextId, setTranslation,
  loadDraft, resetDraft, markDraftSaved,
  bulkStart, applyTranslations, bulkError,
  bulkStopping, bulkPausing, bulkPaused, bulkResume, bulkFinish,
} = translateSlice.actions;

export default store;
